import { useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { useUserData } from "../../contexts/user-data-context";
import { useAnalytics } from "../../lib/analytics";
import { api } from "../../lib/api";
import { useClientTranslations } from "../../lib/i18n/client";
import { isValidAvatarUrl } from "../../lib/utils/avatarUrl";
import { dispatchProfileImageUpdate } from "../../lib/utils/profile-events";
import styles from "./ProfileImageUploader.module.css";

interface ProfileImageUploaderProps {
	initialImageUrl?: string;
	user?: { id: string; displayName?: string | null };
}

export function ProfileImageUploader({
	initialImageUrl = "",
	user,
}: ProfileImageUploaderProps) {
	const inputId = useId();
	const { logProfile } = useAnalytics();

	const [previewUrl, setPreviewUrl] = useState<string>(initialImageUrl);
	const [isImageUploading, setIsImageUploading] = useState(false);
	const { refetchUserData, userData } = useUserData();
	const { t } = useClientTranslations("profile");

	// Update preview URL when initial image URL changes
	useEffect(() => {
		setPreviewUrl(initialImageUrl);
	}, [initialImageUrl]);

	const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			try {
				setIsImageUploading(true);

				const formData = new FormData();
				formData.append("file", file);

				// Use centralized API client for file uploads
				const response = await api.post<{ url: string; success: boolean }>(
					"/api/upload",
					formData,
					{
						headers: {
							"Content-Type": "multipart/form-data",
						},
					},
				);

				const data = response.data;

				// Instant feedback with the fresh presigned URL the upload returns.
				setPreviewUrl(data.url);

				// `/api/upload` already persists the avatar (S3 key) server-side and
				// presigns it on read, so we must NOT store the short-lived presigned
				// URL ourselves (it expires ~1h → blank avatar). Instead refresh user
				// data so the canonical, freshly-presigned `userImage` flows from
				// GET /api/user to every consumer (Header, SideMenu, …).
				const userId = user?.id || userData?.id;
				if (userId) {
					dispatchProfileImageUpdate(userId, data.url);
				}
				await refetchUserData();

				logProfile("image_uploaded", {
					success: true,
					file_size: file.size,
					file_type: file.type,
				});

				toast.success(t("messages.profileImageUpdated"));
			} catch (error) {
				console.error("Error uploading image:", error);
				toast.error(t("messages.profileImageFailed"));

				logProfile("image_upload_failed", {
					error: error instanceof Error ? error.message : "Unknown error",
					file_size: file.size,
					file_type: file.type,
				});
			} finally {
				setIsImageUploading(false);
			}
		}
	};

	return (
		<div className={styles.container}>
			<div className={styles.imageWrapper}>
				<div className={styles.imageCircle}>
					{isImageUploading ? (
						<div className={styles.loading}>
							<svg
								className={styles.spinner}
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
							>
								<title>Loading</title>
								<circle
									className={styles.spinnerCircle}
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									strokeWidth="4"
								/>
								<path
									className={styles.spinnerPath}
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
								/>
							</svg>
						</div>
					) : isValidAvatarUrl(previewUrl) ? (
						<img
							src={previewUrl}
							alt={t("messages.profileImageAlt")}
							className={styles.image}
						/>
					) : (
						<img
							src="/images/era-icon-black.svg"
							alt="Era"
							className={styles.image}
						/>
					)}
				</div>
				<label
					htmlFor={inputId}
					className={`${styles.editButton} ${isImageUploading ? styles.disabled : ""}`}
				>
					<svg
						className={styles.editIcon}
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<title>Edit</title>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
						/>
					</svg>
					<input
						id={inputId}
						type="file"
						accept="image/*"
						className={styles.hiddenInput}
						onChange={handleImageChange}
						disabled={isImageUploading}
					/>
				</label>
			</div>
		</div>
	);
}
